import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Container,
  Divider,
  Link,
  Fade,
  useTheme,
} from "@mui/material";
import UnauthenticatedPageLayout from "../shared/UnauthenticatedPageLayout";
import VerifyEmailStep from "./VerifyEmailStep";
import { useBranding } from "../../branding/BrandingProvider";
import Form from "../Forms/Form";
import { FormField } from "../Forms/FormField";
import { FieldTypes } from "../Forms/FieldTypes";
import * as Yup from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import useApi from "@/hooks/useApi";
import useApiError from "@/common/hooks/useApiError";

dayjs.extend(utc);

function GetFormFields() {
  const fields: FormField[] = [
    {
      id: "firstName",
      text: "First Name",
      columns: 6,
      type: FieldTypes.Text,
      yupSchema: Yup.string()
        .required("Required")
        .max(50, "Must be 50 characters or less"),
    },
    {
      id: "lastName",
      text: "Last Name",
      columns: 6,
      type: FieldTypes.Text,
      yupSchema: Yup.string()
        .required("Required")
        .max(50, "Must be 50 characters or less"),
    },
    {
      id: "dob",
      text: "Date of Birth",
      columns: 12,
      type: FieldTypes.DatePicker,
      yupSchema: Yup.date()
        .required("Required")
        .typeError("Required")
        .min(new Date(1900, 1, 1), "Are you sure?")
        .max(
          dayjs().subtract(18, "years").startOf("day").utc(),
          "Must be at least 18 years old",
        ),
    },
    {
      id: "gender",
      text: "Gender",
      columns: 12,
      type: FieldTypes.DropDownList,
      choices: [
        { id: "1", text: "Male", value: 1 },
        { id: "2", text: "Female", value: 2 },
      ],
      yupSchema: Yup.string().required("Required"),
    },
    {
      id: "email",
      email: true,
      text: "Email",
      columns: 12,
      type: FieldTypes.Text,
      yupSchema: Yup.string()
        .email("Invalid email address")
        .required("Required")
        .max(254, "Email address is too long"),
    },
    {
      id: "zipCode",
      text: "ZIP Code",
      columns: 12,
      type: FieldTypes.Text,
      yupSchema: Yup.string()
        .required("Required")
        .matches(/^\d{5}(-\d{4})?$/, "Please enter a valid ZIP code")
        .max(10, "Invalid ZIP code"),
    },
  ];

  return fields;
}

const NewPatientSelfSignup: React.FC = () => {
  const brand = useBranding();
  const theme = useTheme();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isInUnsupportedArea, setIsInUnsupportedArea] = useState(false);
  const [serverMessage, setServerMessage] = useState("");
  const [hasOptedInForNotification, setHasOptedInForNotification] =
    useState(false);
  const [userEmail, setUserEmail] = useState("");

  const api = useApi(false);
  const { addError } = useApiError();

  const createAccount = async (values: Record<string, unknown>) => {
    try {
      // Convert DOB from dayjs object to DateOnly-compatible string format
      const payload = {
        ...values,
        dob: values.dob ? dayjs(values.dob as string).format("YYYY-MM-DD") : values.dob,
      };

      const response = await api.post<{
        isServiceAreaSupported: boolean;
        message?: string;
      }>(`/invitation/patient`, payload);

      if (response.status === 200) {
        const { isServiceAreaSupported, message } = response.data;

        if (isServiceAreaSupported) {
          setUserEmail(values.email as string); // For the resend below
          setIsSubmitted(true);
        } else {
          setIsInUnsupportedArea(true);
          setServerMessage(message ?? "");
          setUserEmail(values.email as string); // Store email for later opt-in
        }
      } else {
        addError(response.data);
      }
    } catch (error) {
      addError(error);
    }
  };

  const [resending, setResending] = useState(false);
  const [resendConfirmation, setResendConfirmation] = useState<string | null>(null);

  const resendInvitation = async () => {
    setResending(true);
    try {
      const response = await api.post<{ message?: string }>(
        `/invitation/patient/resend`,
        { email: userEmail },
      );
      setResendConfirmation(
        response.data?.message ?? "We've sent the email again.",
      );
    } catch (error) {
      addError(error);
    } finally {
      setResending(false);
    }
  };

  const optInForNotification = async () => {
    try {
      const response = await api.post(
        `/invitation/interested-user-notification`,
        { email: userEmail },
      );

      if (response.status === 200) {
        setHasOptedInForNotification(true);
      } else {
        addError(response.data);
      }
    } catch (error) {
      addError(error);
    }
  };

  return (
    <UnauthenticatedPageLayout hideAppBar>
      <Container maxWidth="xl">
        <Box sx={{ py: 8 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 4,
            }}
          >
            <img
              src={brand.logo}
              alt={`${brand.displayName} logo`}
              style={{ height: "80px" }}
            />
          </Box>

          <Typography
            variant="h2"
            sx={{
              color: "primary.main",
              fontWeight: 700,
              mb: 4,
              textAlign: "center",
            }}
          >
            {isSubmitted
              ? "Verify your email"
              : isInUnsupportedArea
                ? "Service Area Notification"
                : "Create a New Account"}
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              width: "100%",
              maxWidth: 600,
              mx: "auto",
              borderRadius: 2,
              boxShadow: (theme) => theme.palette.brand.shadow.raised,
            }}
          >
            <Fade in={!isSubmitted && !isInUnsupportedArea} timeout={500}>
              <Box
                sx={{
                  display:
                    isSubmitted || isInUnsupportedArea ? "none" : "block",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.primary",
                    mb: 3,
                    textAlign: "center",
                    maxWidth: 600,
                    mx: "auto",
                    fontWeight: 500,
                  }}
                >
                  Start your journey to better headache management. After
                  creating your account, you'll complete a brief questionnaire
                  to help us understand your symptoms and develop a personalized
                  treatment plan.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Form
                  onSubmit={createAccount}
                  fields={GetFormFields()}
                  submitButtonText="Create Account"
                  centerActionButtons
                />

                <Box sx={{ mt: 3, textAlign: "center" }}>
                  <Link
                    href="/"
                    sx={{
                      color: "text.secondary",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Already have an account?
                  </Link>
                </Box>
              </Box>
            </Fade>

            <Fade in={isSubmitted} timeout={500}>
              <Box sx={{ display: isSubmitted ? "block" : "none" }}>
                {/* Finish here: code + password on this page (2026-10-05). NEW-57(a)/(b) live on
                    in the copy (the email may take a minute) and the one-click resend. */}
                {isSubmitted && (
                  <VerifyEmailStep
                    email={userEmail}
                    onResend={resendInvitation}
                    resending={resending}
                    resendConfirmation={resendConfirmation}
                  />
                )}
              </Box>
            </Fade>

            <Fade in={isInUnsupportedArea} timeout={500}>
              <Box
                sx={{
                  display: isInUnsupportedArea ? "block" : "none",
                  textAlign: "center",
                  py: 4,
                }}
              >
                {!hasOptedInForNotification ? (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "text.primary",
                        mb: 3,
                        textAlign: "center",
                        maxWidth: 600,
                        mx: "auto",
                        fontWeight: 500,
                      }}
                    >
                      {serverMessage}
                    </Typography>
                    <Box
                      sx={{
                        mt: 3,
                        display: "flex",
                        gap: 2,
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={optInForNotification}
                        style={{
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          border: "none",
                          padding: "12px 24px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "16px",
                          fontWeight: 500,
                        }}
                      >
                        Yes, notify me when available
                      </button>
                      <Link
                        href="/"
                        style={{
                          display: "inline-block",
                          padding: "12px 24px",
                          color: theme.palette.text.secondary,
                          textDecoration: "none",
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: "8px",
                          fontSize: "16px",
                          fontWeight: 500,
                        }}
                      >
                        No thanks
                      </Link>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "text.primary",
                        mb: 3,
                        textAlign: "center",
                        maxWidth: 600,
                        mx: "auto",
                        fontWeight: 500,
                      }}
                    >
                      Thank you for your interest! We'll notify you as soon as
                      we expand to your area.
                    </Typography>
                    <Link
                      href="/"
                      style={{
                        display: "inline-block",
                        padding: "12px 24px",
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        textDecoration: "none",
                        borderRadius: "8px",
                        fontSize: "16px",
                        fontWeight: 500,
                      }}
                    >
                      Return to Home
                    </Link>
                  </>
                )}
              </Box>
            </Fade>
          </Paper>
        </Box>
      </Container>
    </UnauthenticatedPageLayout>
  );
};

export default NewPatientSelfSignup;
